import type { Editor } from "@tiptap/react";
import { EditorContent } from "@tiptap/react";
import { CloudUpload } from "lucide-react";
import type { DragEventHandler, ReactNode, RefObject } from "react";
import { cn } from "@/lib/utils";

interface MessageEditorRegionProps {
  editor: Editor | null;
  hasContent: boolean;
  placeholder: string;
  className?: string;
  placeholderClassName?: string;
  scrollRef?: RefObject<HTMLDivElement | null>;
  showCharacterCounter?: boolean;
  characterCountLabel?: string;
  characterLimitExceeded?: boolean;
}

export function MessageEditorRegion({
  editor,
  hasContent,
  placeholder,
  className,
  placeholderClassName,
  scrollRef,
  showCharacterCounter = false,
  characterCountLabel,
  characterLimitExceeded = false,
}: MessageEditorRegionProps) {
  return (
    <div ref={scrollRef} className={className}>
      {editor && !hasContent ? (
        <p
          className={cn(
            "pointer-events-none absolute left-0 top-0 text-sm text-muted-foreground",
            placeholderClassName
          )}
        >
          {placeholder}
        </p>
      ) : null}
      <EditorContent editor={editor} className="ph-no-capture" />
      {showCharacterCounter && characterCountLabel ? (
        <p
          className={cn(
            "mt-1 text-right text-[11px] leading-none text-muted-foreground",
            characterLimitExceeded && "text-destructive"
          )}
        >
          {characterCountLabel}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Whole-composer drag-and-drop drop target (design state 2). When `active`, an overlay covers the
 * composer with an accent border + tinted surface + centred hint, and the composer grows to a
 * comfortable drop size. Handlers are optional: when the composer self-manages drags they are
 * spread onto the box so a drop anywhere over it is caught; when an ancestor owns the drop (the
 * whole chat area), only `active` is provided and the ancestor handles the drop.
 */
export interface MessageComposerDropzone {
  active: boolean;
  hint?: string;
  onDragEnter?: DragEventHandler<HTMLDivElement>;
  onDragOver?: DragEventHandler<HTMLDivElement>;
  onDragLeave?: DragEventHandler<HTMLDivElement>;
  onDrop?: DragEventHandler<HTMLDivElement>;
}

interface MessageComposerLayoutProps {
  className?: string;
  expandedView: boolean;
  recordingCompact: boolean;
  recordingWithText: boolean;
  /** Transcribing mirrors recording: it takes over the same row(s) after the recorder stops. */
  transcribingCompact?: boolean;
  transcribingWithText?: boolean;
  attachSlot?: ReactNode;
  editorRegion: ReactNode;
  recordingStrip: ReactNode;
  recordingStripWithText: ReactNode;
  transcribingStrip?: ReactNode;
  transcribingStripWithText?: ReactNode;
  rightCluster: ReactNode;
  attachmentsStrip?: ReactNode;
  dropzone?: MessageComposerDropzone;
  showFooter: boolean;
  footerCaption: ReactNode;
  contextIndicator?: ReactNode;
}

export function MessageComposerLayout({
  className,
  expandedView,
  recordingCompact,
  recordingWithText,
  transcribingCompact = false,
  transcribingWithText = false,
  attachSlot,
  editorRegion,
  recordingStrip,
  recordingStripWithText,
  transcribingStrip,
  transcribingStripWithText,
  rightCluster,
  attachmentsStrip,
  dropzone,
  showFooter,
  footerCaption,
  contextIndicator,
}: MessageComposerLayoutProps) {
  // Compact = the strip replaces the input row entirely; withText = text stays above the strip.
  const stripCompact = recordingCompact || transcribingCompact;
  const stripWithText = recordingWithText || transcribingWithText;
  return (
    <div className="flex w-full flex-col gap-2">
      {/* The box is always a column: an optional attachments tray sits on top, a 1px divider
          separates it from the input row below — one card, one border (design states 3-6). */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: the composer card is a file drop surface, not an interactive control; attaching is also available via the keyboard-accessible attach menu. */}
      <div
        className={cn(
          "relative flex flex-col border bg-background transition-[min-height] duration-200 ease-out",
          // While dragging files, grow the (otherwise one-row) composer into a comfortable ~4-row
          // drop target so it's easy to hit; it animates back down on drop / drag-leave.
          dropzone?.active && "min-h-52",
          className
        )}
        onDragEnter={dropzone?.onDragEnter}
        onDragOver={dropzone?.onDragOver}
        onDragLeave={dropzone?.onDragLeave}
        onDrop={dropzone?.onDrop}
      >
        {attachmentsStrip ? (
          <>
            <div className="min-w-0 p-3">{attachmentsStrip}</div>
            <div className="h-px w-full bg-border" />
          </>
        ) : null}
        <div className={cn("flex min-w-0", expandedView ? "flex-col" : "items-center gap-2 p-2.5")}>
          {/* Keep editorRegion mounted at this fixed slot so TipTap React node-view portals survive layout flips. */}
          {attachSlot ? (
            <div
              className={expandedView || stripCompact ? "hidden" : "contents"}
              data-testid="message-composer-leading-action"
            >
              {attachSlot}
            </div>
          ) : null}
          <div className={stripCompact ? "hidden" : "contents"}>{editorRegion}</div>
          <div className={recordingCompact ? "contents" : "hidden"}>{recordingStrip}</div>
          <div className={recordingWithText ? "contents" : "hidden"}>{recordingStripWithText}</div>
          <div className={transcribingCompact ? "contents" : "hidden"}>{transcribingStrip}</div>
          <div className={transcribingWithText ? "contents" : "hidden"}>
            {transcribingStripWithText}
          </div>
          <div className={stripCompact || stripWithText ? "hidden" : "contents"}>
            {expandedView ? (
              <div className="flex items-center justify-between px-2.5 pb-2.5 pt-1.5">
                {attachSlot ? (
                  <div className="contents" data-testid="message-composer-leading-action">
                    {attachSlot}
                  </div>
                ) : (
                  <span />
                )}
                {rightCluster}
              </div>
            ) : (
              rightCluster
            )}
          </div>
        </div>
        {dropzone?.active ? (
          <div
            className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-1.5 border-2 border-primary bg-background text-center"
            data-testid="message-composer-dropzone"
          >
            {/* Opaque box hides the composer behind it; this faint tint gives the accent wash. */}
            <div className="absolute inset-0 bg-primary/5" />
            <CloudUpload className="relative size-7 text-muted-foreground" />
            <span className="relative text-sm font-semibold text-foreground">
              Drop files to attach
            </span>
            {dropzone.hint ? (
              <span className="relative text-xs text-muted-foreground">{dropzone.hint}</span>
            ) : null}
          </div>
        ) : null}
      </div>
      {showFooter ? (
        <div className="flex items-center justify-between gap-2 px-0.5">
          <span className="text-neutral-dim min-w-0 flex-1 truncate text-[11px] leading-snug">
            {footerCaption}
          </span>
          {contextIndicator ? <div className="shrink-0">{contextIndicator}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

interface LegacyMessageRichTextLayoutProps {
  className?: string;
  compact: boolean;
  isInlineLayout: boolean;
  toolbar: ReactNode;
  editorRegion: ReactNode;
  attachmentButton: ReactNode;
  actionButtons: ReactNode;
}

export function LegacyMessageRichTextLayout({
  className,
  compact,
  isInlineLayout,
  toolbar,
  editorRegion,
  attachmentButton,
  actionButtons,
}: LegacyMessageRichTextLayoutProps) {
  return (
    <div
      className={cn(
        "rounded-xl border bg-background shadow-sm",
        isInlineLayout ? (compact ? "min-h-9 px-3 py-2" : "px-3 py-2") : "p-3",
        className
      )}
    >
      {toolbar}

      {isInlineLayout ? (
        <div className="flex items-end gap-2">
          {editorRegion}
          <div
            className={cn(
              "flex items-center",
              compact ? "gap-2 [&>button]:!size-4 [&>button]:!p-0" : "gap-1 pb-0.5"
            )}
          >
            {attachmentButton}
            {actionButtons}
          </div>
        </div>
      ) : (
        <>
          {editorRegion}
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-1">{attachmentButton}</div>
            <div className="flex items-center gap-1">{actionButtons}</div>
          </div>
        </>
      )}
    </div>
  );
}
